import type { BoardDefinition } from "../types/game-data-config";

// Canonical War Chest board geometry — single authoritative source of truth.
// Both engine board generation and renderers (playground + multiplayer client)
// import from this module. Never duplicate COL_DEFS, DIR_VECTORS, or LOCATIONS.
//
// Location layer is at v3 (corrected per docs/.../warchest_correct_board_locations_v3.svg):
//   - 2P play uses LOCATIONS_2P (10 entries: 4 starting + 6 neutral)
//   - 4P adds SIDE_LOCATIONS_4P_ONLY (4 dark-edge entries) → LOCATIONS_4P (14)
//   - The board geometry (COL_DEFS, projection, DIR_VECTORS) is unchanged.
//
// Earlier drafts placed markers on (0,0), (0,-3), (0,3), and along the centre
// row — those are wrong. The v3 reference SVG and the patch from the user
// are the authoritative source; do not infer additional markers from
// symmetry. See LOCATIONS_2P comment below.

export type ColMode = "play" | "edge";

export interface ColDef {
  readonly col: number;
  readonly h: number;
  readonly mode: ColMode;
}

// 11 flat-top columns, heights 2-3-4-5-6-7-6-5-4-3-2 (47 hexes total).
// 37 cream play hexes form the 2-player board; the 10 darker outer hexes
// (col ±4, ±5) are 4-player territory and are hidden in 2P render.
export const COL_DEFS: readonly ColDef[] = [
  { col: -5, h: 2, mode: "edge" },
  { col: -4, h: 3, mode: "edge" },
  { col: -3, h: 4, mode: "play" },
  { col: -2, h: 5, mode: "play" },
  { col: -1, h: 6, mode: "play" },
  { col: 0, h: 7, mode: "play" },
  { col: 1, h: 6, mode: "play" },
  { col: 2, h: 5, mode: "play" },
  { col: 3, h: 4, mode: "play" },
  { col: 4, h: 3, mode: "edge" },
  { col: 5, h: 2, mode: "edge" },
];

export type Direction = "N" | "S" | "NE" | "SE" | "SW" | "NW";

// Pure axial direction deltas. Row may step by 0.5 — never branch on column
// parity. Iterating the same vector for multi-step moves always lands on a
// valid (integer or half-integer) row given the alternating column heights.
export const DIR_VECTORS: Readonly<
  Record<Direction, readonly [number, number]>
> = {
  N: [0, -1],
  S: [0, 1],
  NE: [1, -0.5],
  SE: [1, 0.5],
  SW: [-1, 0.5],
  NW: [-1, -0.5],
};

// 2-player starting locations.
// Top faction (Raven, P1) holds two markers along the upper edge.
export const STARTING_TOP_2P: ReadonlySet<string> = new Set([
  "-1,-2.5",
  "2,-2",
]);
// Bottom faction (Wolf, P2) holds two markers along the lower edge.
export const STARTING_BOTTOM_2P: ReadonlySet<string> = new Set([
  "-2,2",
  "1,2.5",
]);

// 2-player neutral locations — six contestable hexes inside the cream play area.
export const NEUTRAL_LOCATIONS_2P: ReadonlySet<string> = new Set([
  "-2,-1",
  "-3,0.5",
  "-1,0.5",
  "1,-0.5",
  "3,-0.5",
  "2,1",
]);

// All 2-player locations (10 = 2 + 2 + 6).
//
// WHY: This is the v3 canonical layout from
// docs/.../warchest_correct_board_locations_v3.svg. Earlier drafts placed
// markers on (0,0), (0,-3), and (0,3) along a horizontal centre row, but the
// reference shows two staggered starts per faction at rows ±2 / ±2.5 and a
// rotational neutral pattern. Markers are NOT mirror-symmetric under
// (col, row) → (col, -row); do not infer additional markers from symmetry.
export const LOCATIONS_2P: ReadonlySet<string> = new Set([
  ...STARTING_TOP_2P,
  ...STARTING_BOTTOM_2P,
  ...NEUTRAL_LOCATIONS_2P,
]);

// 4-player-only side locations — four markers on the darker outer columns.
// Hidden in 2-player render; not valid action targets in 2P mode.
export const SIDE_LOCATIONS_4P_ONLY: ReadonlySet<string> = new Set([
  "-4,-1",
  "-5,0.5",
  "5,-0.5",
  "4,1",
]);

// All 4-player locations (14 = 10 + 4).
export const LOCATIONS_4P: ReadonlySet<string> = new Set([
  ...LOCATIONS_2P,
  ...SIDE_LOCATIONS_4P_ONLY,
]);

// Backward-compat aliases for the engine (uses Raven=top=P1, Wolf=bottom=P2).
export const STARTING_RAVEN = STARTING_TOP_2P;
export const STARTING_WOLF = STARTING_BOTTOM_2P;
// Default 2P-mode locations alias used by generateBaseBoard and renderers.
export const LOCATIONS = LOCATIONS_2P;

// Canonical hex ID format: "col,row". Half-integer rows survive verbatim
// (-2.5 stays -2.5); never coerce to integer. Negative-zero is normalised
// to 0 so "0,0" cannot become "-0,0".
export function formatHexId(col: number, row: number): string {
  const c = Object.is(col, -0) ? 0 : col;
  const r = Object.is(row, -0) ? 0 : row;
  return `${c},${r}`;
}

export function parseHexId(id: string): readonly [number, number] {
  const idx = id.indexOf(",");
  return [Number(id.slice(0, idx)), Number(id.slice(idx + 1))];
}

// Flat-top hex pixel projection — impl guide §2.
// dx = size * 1.5, dy = size * sqrt(3). Pointy-top math rotates the board 30°
// and breaks every hex ID; do not invent a variant.
export function colRowToPx(
  col: number,
  row: number,
  size: number,
): readonly [number, number] {
  return [col * size * 1.5, row * size * Math.sqrt(3)];
}

// SVG path for a flat-top hex centered at (cx, cy) with edge length `size`.
// Vertices at 0°, 60°, 120°, 180°, 240°, 300°.
export function hexPath(cx: number, cy: number, size: number): string {
  const parts: string[] = [];
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 180) * (60 * i);
    const x = cx + size * Math.cos(a);
    const y = cy + size * Math.sin(a);
    parts.push(`${i === 0 ? "M" : "L"}${x},${y}`);
  }
  parts.push("Z");
  return parts.join(" ");
}

function rowsForColumn(h: number): readonly number[] {
  const half = (h - 1) / 2;
  const rows: number[] = [];
  for (let i = 0; i < h; i += 1) {
    rows.push(-half + i);
  }
  return rows;
}

export interface HexCoord {
  readonly col: number;
  readonly row: number;
  readonly mode: ColMode;
}

export function listHexCoords(filter?: ColMode): readonly HexCoord[] {
  const out: HexCoord[] = [];
  for (const def of COL_DEFS) {
    if (filter && def.mode !== filter) continue;
    for (const row of rowsForColumn(def.h)) {
      out.push({ col: def.col, row, mode: def.mode });
    }
  }
  return out;
}

export const ALL_HEX_IDS: readonly string[] = listHexCoords().map(
  ({ col, row }) => formatHexId(col, row),
);

export const PLAY_HEX_IDS: readonly string[] = listHexCoords("play").map(
  ({ col, row }) => formatHexId(col, row),
);

export const EDGE_HEX_IDS: readonly string[] = listHexCoords("edge").map(
  ({ col, row }) => formatHexId(col, row),
);

// Set forms for O(1) membership checks (validators, location auditors).
export const ALL_HEX_ID_SET: ReadonlySet<string> = new Set(ALL_HEX_IDS);
export const PLAY_HEX_ID_SET: ReadonlySet<string> = new Set(PLAY_HEX_IDS);
export const EDGE_HEX_ID_SET: ReadonlySet<string> = new Set(EDGE_HEX_IDS);

// Canonical 2-player BoardDefinition: 37 play hexes, axial-derived adjacency.
// Edge hexes (4P territory) are excluded; renderers may still display them
// from COL_DEFS as static decoration.
export function generateBaseBoard(): BoardDefinition {
  const hexes = PLAY_HEX_IDS;
  const adjacency: Record<string, string[]> = {};
  for (const id of hexes) {
    const [col, row] = parseHexId(id);
    const neighbors: string[] = [];
    for (const [dc, dr] of Object.values(DIR_VECTORS)) {
      const nid = formatHexId(col + dc, row + dr);
      if (PLAY_HEX_ID_SET.has(nid)) neighbors.push(nid);
    }
    adjacency[id] = neighbors;
  }
  return {
    id: "base-2p",
    hexes,
    locations: [...LOCATIONS_2P],
    adjacency,
    startLocations: {
      P1: [...STARTING_TOP_2P],
      P2: [...STARTING_BOTTOM_2P],
    },
  };
}

// Runtime invariant check for the location layer.
// Run from tests (and optionally at engine init in dev mode) to catch any
// drift from the v3 canonical. Throws with a precise message on first
// failure; used as a single guard against partial / merge-conflict edits
// reverting to an older location set.
export function assertWarChestLocationContext(): void {
  if (LOCATIONS_2P.size !== 10) {
    throw new Error(`Expected 10 2P locations, got ${LOCATIONS_2P.size}`);
  }
  if (STARTING_TOP_2P.size !== 2) {
    throw new Error(
      `Expected 2 top starting locations, got ${STARTING_TOP_2P.size}`,
    );
  }
  if (STARTING_BOTTOM_2P.size !== 2) {
    throw new Error(
      `Expected 2 bottom starting locations, got ${STARTING_BOTTOM_2P.size}`,
    );
  }
  if (NEUTRAL_LOCATIONS_2P.size !== 6) {
    throw new Error(
      `Expected 6 neutral 2P locations, got ${NEUTRAL_LOCATIONS_2P.size}`,
    );
  }
  if (SIDE_LOCATIONS_4P_ONLY.size !== 4) {
    throw new Error(
      `Expected 4 side-only 4P locations, got ${SIDE_LOCATIONS_4P_ONLY.size}`,
    );
  }
  if (LOCATIONS_4P.size !== 14) {
    throw new Error(`Expected 14 4P locations, got ${LOCATIONS_4P.size}`);
  }
  for (const id of LOCATIONS_2P) {
    if (!PLAY_HEX_ID_SET.has(id)) {
      throw new Error(`2P location must be inside play area: ${id}`);
    }
  }
  for (const id of SIDE_LOCATIONS_4P_ONLY) {
    if (!ALL_HEX_ID_SET.has(id)) {
      throw new Error(`4P side location does not exist on board: ${id}`);
    }
    if (PLAY_HEX_ID_SET.has(id)) {
      throw new Error(`4P side location must not be a 2P play hex: ${id}`);
    }
  }
  // Coordinates carried in earlier drafts that must not regress.
  const REMOVED_LOCATIONS = [
    // v1 phantoms (impl-guide §3 typos)
    "-3,0",
    "3,0",
    // v2 draft locations replaced by v3
    "-2,-2",
    "0,-3",
    "0,0",
    "0,3",
    "2,2",
    "-1,-1.5",
    "1,-1.5",
    "-1,1.5",
    "1,1.5",
    // older intermediate coordinates
    "-5,-0.5",
    "-2,1",
    "2,-1",
    "5,0.5",
  ];
  for (const id of REMOVED_LOCATIONS) {
    if (
      LOCATIONS_2P.has(id) ||
      SIDE_LOCATIONS_4P_ONLY.has(id) ||
      LOCATIONS_4P.has(id)
    ) {
      throw new Error(`Old incorrect location still present: ${id}`);
    }
  }
}
